import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import {
  Landmark,
  TrendingDown,
  Wallet,
  Shield,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";

const benefits = [
  {
    icon: TrendingDown,
    title: "Avoid Selling During Dips",
    description: "Keep your crypto holdings while accessing liquidity during market downturns.",
  },
  {
    icon: Wallet,
    title: "Access Short-term Liquidity",
    description: "Get funds when you need them without liquidating your investments.",
  },
  {
    icon: Shield,
    title: "Flexible Use of Funds",
    description: "Use borrowed funds for any purpose — emergencies, opportunities, or expenses.",
  },
];

export default function CryptoLoans() {
  return (
    <div className="min-h-screen bg-background">
      <NavBar />

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-primary/5" />
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[150px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 mb-6">
              <Landmark className="w-4 h-4 text-accent" />
              <span className="text-sm text-accent font-medium">Educational Guide</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black mb-6">
              Crypto-Backed{" "}
              <span className="text-accent">Loans</span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground mb-8">
              Unlock liquidity without selling your assets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What is a Crypto Loan */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 md:p-12"
          >
            <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-6 text-center">
              What is a <span className="text-accent">Crypto Loan</span>?
            </h2>
            <p className="text-lg text-muted-foreground text-center leading-relaxed">
              Borrow funds by using your cryptocurrency as collateral, while retaining ownership 
              of your assets. When you repay the loan, your crypto is returned to you. It's a way 
              to access liquidity without triggering a taxable event or missing out on potential gains.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Use Crypto Loans */}
      <section className="py-20 relative bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Why Use <span className="text-accent">Crypto Loans</span>?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card rounded-2xl p-8 hover:border-accent/30 transition-colors"
                >
                  <div className="w-14 h-14 rounded-xl bg-accent/20 flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="text-xl font-heading font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PassivePay's Role */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 md:p-12 border-primary/30"
          >
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-6">
                PassivePay's <span className="text-primary">Role</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                PassivePay provides crypto-backed loans directly to our users. We offer 
                competitive rates, transparent terms, and a secure lending experience. 
                Our team guides you through responsible borrowing practices while helping 
                you understand LTV ratios and risk management.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="px-4 py-2 rounded-lg bg-primary/10 border border-primary/20 text-sm">
                  Direct Lending
                </div>
                <div className="px-4 py-2 rounded-lg bg-primary/10 border border-primary/20 text-sm">
                  Competitive Rates
                </div>
                <div className="px-4 py-2 rounded-lg bg-primary/10 border border-primary/20 text-sm">
                  Secure & Transparent
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Loan Terms */}
      <section className="py-20 relative bg-card/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Loan <span className="text-accent">Terms</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card rounded-xl p-6"
            >
              <div className="text-sm text-muted-foreground mb-2">Accepted Collateral</div>
              <div className="text-2xl font-heading font-bold text-primary">BTC & ETH Only</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card rounded-xl p-6"
            >
              <div className="text-sm text-muted-foreground mb-2">Loan Currency</div>
              <div className="text-2xl font-heading font-bold text-accent">USDT</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card rounded-xl p-6"
            >
              <div className="text-sm text-muted-foreground mb-2">Maximum LTV</div>
              <div className="text-2xl font-heading font-bold text-primary">50%</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-card rounded-xl p-6"
            >
              <div className="text-sm text-muted-foreground mb-2">Interest Rate</div>
              <div className="text-2xl font-heading font-bold text-accent">13%</div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-xl p-6 border-yellow-500/30 bg-yellow-500/5"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-yellow-500">Important:</span> The collateral will automatically 
                be sold if the loan is not paid back by the defined duration.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Risk Note */}
      <section className="py-12 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-xl p-6 border-red-500/30 bg-red-500/5"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-heading font-bold mb-2 text-red-400">Risk Warning</h4>
                <p className="text-sm text-muted-foreground">
                  Crypto loans involve liquidation risk if prices fall below your collateral threshold. 
                  Always understand the Loan-to-Value (LTV) ratio and maintain adequate collateral. 
                  Never borrow more than you can afford to lose.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
